import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { ColorPicker } from "@/modern/components/color-picker";
import { IconSelector } from "../components/IconSelector";
import {
  ViewEditorSection,
  VIEW_EDITOR_FIELD_GRID_CLASS,
  VIEW_EDITOR_SECTION_STACK_CLASS
} from "../components/ViewEditorSection";
import { ViewEditorFormField } from "../components/ViewEditorFormField";
import { VIEW_STATE_PIITS_UPDATE_LISTENER, ViewFolderTypes } from "@/other/constants/OtherKeys.jsx";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import { Settings } from "lucide-react";
import { useViewEditor } from "../context/ViewEditorContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

const ManageView = () => {
  const [swivuSaveButtonStateListener, setSwivuSaveButtonStateListener] = useState<(() => void) | null>(null);
  const context = useSwivuBryntumContext();
  const { record, isSaveAsView = false, canEditView, form, formState, focusViewNameRef, saveAsNameError } = useViewEditor();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [currentSelectedColumnInCombo, setCurrentSelectedColumnInCombo] = useState<number | null>(record.folderId);
  const [folders, setFolders] = useState<Array<{ id: number; name: string }>>([]);
  const [icons, setIcons] = useState<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any

  const folderId = form.watch("folderId");
  const color = form.watch("color");
  const iconId = form.watch("iconId");

  useEffect(() => {
    const folderData = context.project.viewsFolderStore.query(
      // @ts-expect-error typescript does not work as expected with bryntum
      (folder: any) => ({
        id: folder.id,
        name: folder.name
      }),
      true
    );

    // @ts-expect-error typescript does not work as expected with bryntum
    setFolders(folderData);

    const iconData = context.project.resourceTagValues.query(
      (icon: any) => icon.tagSet === "Icon", // eslint-disable-line @typescript-eslint/no-explicit-any

      true
    );

    setIcons(iconData);

    if (isSaveAsView) {
      const moreFolder = context.project.viewsFolderStore.find((folder: any) => folder.type === ViewFolderTypes.more); // eslint-disable-line @typescript-eslint/no-explicit-any

      if (moreFolder) {
        form.setValue("folderId", moreFolder.id);

        setCurrentSelectedColumnInCombo(moreFolder.id);
      }
    }
  }, [context, isSaveAsView]);

  useEffect(() => {
    focusViewNameRef.current = () => nameInputRef.current?.focus();

    return () => {
      focusViewNameRef.current = null;
    };
  }, [focusViewNameRef]);

  useEffect(() => {
    if (folderId) {
      updateColumnManagerData(folderId);
    }
  }, [folderId]);

  useEffect(() => {
    if (color) {
      const iconElement = document.querySelector(".icon-selector-icon");

      if (iconElement) {
        (iconElement as HTMLElement).style.color = color;
      }
    }
  }, [color, iconId]);

  useEffect(() => {
    if (!isSaveAsView) {
      const timer = setTimeout(() => {
        const closeButton = document.querySelector('[aria-label="Close"]') as HTMLElement;

        if (closeButton) {
          closeButton.focus();
        }
      }, 200);

      return () => clearTimeout(timer);
    }

    return;
  }, [isSaveAsView]);

  const updateColumnManagerData = (folderId: number) => {
    setCurrentSelectedColumnInCombo(folderId);
  };

  const onColumnManagerSettingsClick = () => {
    // @ts-expect-error typescript does not work as expected with bryntum
    const folderRecord = context.project.viewsFolderStore.getById(currentSelectedColumnInCombo);

    context.editorHandler.openEditor(
      EDITOR_KEYS.viewOrganizerEditor,
      // @ts-expect-error typescript does not work as expected with bryntum
      null,
      { folderRecord },
      {
        useExisting: true,
        by: "recordType"
      }
    );
  };

  useEffect(() => {
    if (record.isOwnView && !swivuSaveButtonStateListener) {
      const listener = context.project.userPreferencesStore.on(VIEW_STATE_PIITS_UPDATE_LISTENER, () => {
        form.trigger();
      });
      setSwivuSaveButtonStateListener(() => listener);
    }

    return () => {
      if (swivuSaveButtonStateListener) {
        swivuSaveButtonStateListener();
      }
    };
  }, [record, context, swivuSaveButtonStateListener]);

  return (
    <div className={VIEW_EDITOR_SECTION_STACK_CLASS}>
      <ViewEditorSection title="Basic" subtitle="Name and describe this view">
        <ViewEditorFormField label="View Name" required error={formState.errors.name?.message}>
          <Controller
            control={form.control}
            name="name"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  ref={(el) => {
                    field.ref(el);
                    (nameInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                  }}
                  variant="background"
                  className="h-swivu-input text-base"
                  autoFocus={isSaveAsView}
                  disabled={!canEditView}
                  style={{ color: color || "inherit" }}
                  placeholder="e.g., Q2 Planning, Sprint Overview..."
                  autoComplete="off"
                />

                {((isSaveAsView && field.value === record.name) || saveAsNameError) && (
                  <p className="text-sm text-destructive mt-1">
                    {saveAsNameError || "Name of view must not match with the one being saved from"}
                  </p>
                )}
              </>
            )}
          />
        </ViewEditorFormField>

        <ViewEditorFormField label="Description">
          <Controller
            control={form.control}
            name="viewDescription"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value || ""}
                disabled={!canEditView}
                className="min-h-[100px] !text-base resize-y"
                placeholder="Add a detailed description..."
                autoComplete="off"
              />
            )}
          />
        </ViewEditorFormField>
      </ViewEditorSection>

      <ViewEditorSection title="Appearance" subtitle="Customize how this view appears in the sidebar">
        <ViewEditorFormField label="Column">
          <div className="flex items-center gap-2">
            <Controller
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <Select
                  value={field.value?.toString() || ""}
                  onValueChange={(val) => field.onChange(parseInt(val))}
                  disabled={!canEditView}
                >
                  <SelectTrigger className="flex-1 h-swivu-input text-base">
                    <SelectValue placeholder="Select a column" />
                  </SelectTrigger>

                  <SelectContent position="popper" sideOffset={5} className="z-[--swivu-bryntum-z-index]">
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id.toString()}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />

            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0 h-swivu-input w-swivu-input hover:bg-accent hover:text-accent-foreground active:scale-95 group"
              onClick={onColumnManagerSettingsClick}
              title="Organize columns"
            >
              <Settings className="h-4 w-4 transition-transform duration-200 group-hover:rotate-90" />
            </Button>
          </div>
        </ViewEditorFormField>

        <div className={VIEW_EDITOR_FIELD_GRID_CLASS}>
          <ViewEditorFormField label="Icon">
            <Controller
              control={form.control}
              name="iconId"
              render={({ field }) => (
                <IconSelector
                  value={field.value}
                  onChange={field.onChange}
                  items={icons}
                  disabled={!canEditView}
                  color={color}
                  defaultIcon={SWIVU_STANDARD_ICONS.defaultView}
                />
              )}
            />
          </ViewEditorFormField>

          <ViewEditorFormField label="View Color">
            <Controller
              control={form.control}
              name="color"
              render={({ field }) => (
                <ColorPicker
                  value={field.value}
                  onChange={(color) => {
                    if (color) field.onChange(color);
                  }}
                  editable={false}
                  className="border-0 shadow-none"
                >
                  <Button
                    disabled={!canEditView}
                    type="button"
                    variant="outline"
                    className="w-full justify-start h-swivu-input text-base px-3"
                  >
                    <div
                      className="h-4 w-4 rounded border border-input flex-shrink-0"
                      style={{ backgroundColor: field.value || "transparent" }}
                    />
                    <span className="truncate">{field.value || "Select"}</span>
                  </Button>
                </ColorPicker>
              )}
            />
          </ViewEditorFormField>
        </div>
      </ViewEditorSection>
    </div>
  );
};

export default ManageView;
